import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Flame, TrendingDown, Calendar, BarChart3, 
  ArrowDown, RefreshCw, ExternalLink, Info,
  Plus, X, Check, AlertCircle, Clock, Hash
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  ResponsiveContainer, Cell, Tooltip as RechartsTooltip 
} from "recharts";
import { API } from "../../constants";

// Format large numbers
const formatNumber = (num) => {
  if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
  return num.toLocaleString();
};

// Custom tooltip for chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface-2 border border-white/10 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium text-sm">{label}</p>
        <p className="text-primary-theme font-bold">
          {formatNumber(payload[0].value)} BURNZ
        </p>
      </div>
    );
  }
  return null;
};

// Burn types for dropdown
const BURN_TYPES = [
  { id: 'monthly', label: 'Monthly Burn', description: 'Regular end-of-month burn' },
  { id: 'claim_fee', label: 'Claim Fee', description: 'From XP to BURNZ conversions' },
  { id: 'partner_fee', label: 'Partner Fee', description: 'From partner campaign payments' },
  { id: 'dump_fee', label: 'Dump Fee', description: 'From NFT dump transactions' },
  { id: 'manual', label: 'Manual Burn', description: 'Admin-initiated burn' },
];

// Record Burn Form Component
const RecordBurnForm = ({ onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    amount: '',
    transaction_hash: '',
    burn_type: 'monthly',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Validate
      if (!formData.amount || parseInt(formData.amount) <= 0) {
        throw new Error('Please enter a valid amount');
      }
      if (!formData.transaction_hash || formData.transaction_hash.length < 10) {
        throw new Error('Please enter a valid transaction hash');
      }

      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/burn/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseInt(formData.amount),
          transaction_hash: formData.transaction_hash,
          burn_type: formData.burn_type,
          notes: formData.notes || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to record burn');
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose && onClose();
      }, 1500);

    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="p-8 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center"
        >
          <Check className="w-8 h-8 text-green-400" />
        </motion.div>
        <h3 className="text-xl font-bold text-white mb-2">Burn Recorded!</h3>
        <p className="text-white/50">The burn transaction has been logged successfully.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Amount */}
      <div>
        <label className="text-sm text-white/70 mb-1.5 block">Burn Amount (BURNZ)</label>
        <div className="relative">
          <Input
            type="number"
            placeholder="e.g., 10000000"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            className="bg-white/5 border-white/10 text-white pr-20"
            required
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">BURNZ</span>
        </div>
        {formData.amount && (
          <p className="text-xs text-primary-theme mt-1">
            = {formatNumber(parseInt(formData.amount) || 0)} BURNZ
          </p>
        )}
      </div>

      {/* Transaction Hash */}
      <div>
        <label className="text-sm text-white/70 mb-1.5 block">Transaction Hash</label>
        <div className="relative">
          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            type="text"
            placeholder="Enter Cardano transaction hash"
            value={formData.transaction_hash}
            onChange={(e) => setFormData({ ...formData, transaction_hash: e.target.value })}
            className="bg-white/5 border-white/10 text-white pl-10 font-mono text-sm"
            required
          />
        </div>
      </div>

      {/* Burn Type */}
      <div>
        <label className="text-sm text-white/70 mb-1.5 block">Burn Type</label>
        <div className="grid grid-cols-2 gap-2">
          {BURN_TYPES.map(type => (
            <button
              key={type.id}
              type="button"
              onClick={() => setFormData({ ...formData, burn_type: type.id })}
              className={`p-3 rounded-lg border text-left transition-all ${
                formData.burn_type === type.id
                  ? 'bg-orange-500/20 border-orange-500/40 text-white'
                  : 'bg-white/5 border-white/10 text-white/70 hover:border-white/20'
              }`}
            >
              <p className="text-sm font-medium">{type.label}</p>
              <p className="text-xs text-white/40">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="text-sm text-white/70 mb-1.5 block">Notes (Optional)</label>
        <Textarea
          placeholder="Add any additional notes about this burn..."
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="bg-white/5 border-white/10 text-white min-h-[80px]"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          className="flex-1 text-white/70 hover:text-white"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 hover:opacity-90 text-white"
        >
          {submitting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Recording...
            </>
          ) : (
            <>
              <Flame className="w-4 h-4 mr-2" />
              Record Burn
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// Recent Burns List
const RecentBurnsList = ({ transactions = [] }) => {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-6 text-white/30">
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No burn transactions recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto">
      {transactions.slice(0, 5).map((tx, i) => (
        <div key={tx.id || i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
              <Flame className="w-4 h-4 text-red-400" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">{formatNumber(tx.amount)} BURNZ</p>
              <p className="text-white/30 text-xs">{tx.burn_type || 'monthly'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white/50 text-xs">
              {new Date(tx.created_at).toLocaleDateString()}
            </p>
            <a 
              href={`https://cardanoscan.io/transaction/${tx.transaction_hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-theme hover:underline flex items-center gap-1 justify-end"
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      ))}
    </div>
  );
};

const BurnTracker = ({ compact = false, isAdmin = false }) => {
  const [burnData, setBurnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);

  const fetchBurnReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/burn/report`);
      if (!response.ok) throw new Error('Failed to fetch burn data');
      const data = await response.json();
      setBurnData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      // Set demo data if API fails
      setBurnData({
        total_supply: 1000000000,
        total_burned: 156000000,
        remaining_supply: 844000000,
        burn_percentage: 15.6,
        remaining_percentage: 84.4,
        monthly_data: [
          { month: '2025-01', label: 'Jan 2025', burned: 12000000 },
          { month: '2025-02', label: 'Feb 2025', burned: 15000000 },
          { month: '2025-03', label: 'Mar 2025', burned: 18000000 },
          { month: '2025-04', label: 'Apr 2025', burned: 14000000 },
          { month: '2025-05', label: 'May 2025', burned: 20000000 },
          { month: '2025-06', label: 'Jun 2025', burned: 16000000 },
          { month: '2025-07', label: 'Jul 2025', burned: 13000000 },
          { month: '2025-08', label: 'Aug 2025', burned: 17000000 },
          { month: '2025-09', label: 'Sep 2025', burned: 11000000 },
          { month: '2025-10', label: 'Oct 2025', burned: 8000000 },
          { month: '2025-11', label: 'Nov 2025', burned: 7000000 },
          { month: '2025-12', label: 'Dec 2025', burned: 5000000 },
        ],
        statistics: {
          active_burn_months: 12,
          avg_monthly_burn: 13000000,
          highest_monthly_burn: 20000000,
          projected_months_to_complete: 65
        },
        recent_transactions: []
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBurnReport();
  }, []);

  const handleRecordSuccess = () => {
    fetchBurnReport();
    setShowRecordModal(false);
  };

  if (loading) {
    return (
      <Card className="bg-surface-2 border-white/5">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-primary-theme" />
        </CardContent>
      </Card>
    );
  }

  // Compact version for sidebar/widgets
  if (compact) {
    return (
      <Card className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border-orange-500/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Flame className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs text-white/50">Total Burned</p>
              <p className="text-lg font-bold text-white">{formatNumber(burnData?.total_burned || 0)}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-white/50">Burn Progress</span>
              <span className="text-primary-theme font-medium">{burnData?.burn_percentage?.toFixed(2) || 0}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${burnData?.burn_percentage || 0}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-white/30">
              <span>{formatNumber(burnData?.total_burned || 0)} burned</span>
              <span>{formatNumber(burnData?.remaining_supply || 0)} remaining</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full version with chart
  return (
    <>
      <Card className="bg-surface-2 border-white/5 overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  $BURNZ Burn Tracker
                  <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">LIVE</Badge>
                </CardTitle>
                <CardDescription className="text-white/50">
                  Deflationary token burn progress
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={fetchBurnReport}
                      className="text-white/50 hover:text-white"
                    >
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Refresh data</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {/* Record Burn Button - Always visible in Command Center */}
              <Button
                onClick={() => setShowRecordModal(true)}
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:opacity-90 text-white"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Record Burn
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 pt-4">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-white/50">Total Supply</span>
              </div>
              <p className="text-xl font-bold text-white">{formatNumber(burnData?.total_supply || 0)}</p>
              <p className="text-xs text-white/30">1 Billion BURNZ</p>
            </div>
            
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl p-4 border border-red-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-4 h-4 text-red-400" />
                <span className="text-xs text-white/50">Total Burned</span>
              </div>
              <p className="text-xl font-bold text-red-400">{formatNumber(burnData?.total_burned || 0)}</p>
              <p className="text-xs text-red-400/50">{burnData?.burn_percentage?.toFixed(2) || 0}% of supply</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-green-400" />
                <span className="text-xs text-white/50">Remaining</span>
              </div>
              <p className="text-xl font-bold text-green-400">{formatNumber(burnData?.remaining_supply || 0)}</p>
              <p className="text-xs text-green-400/50">{burnData?.remaining_percentage?.toFixed(2) || 0}% left</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-white/50">Avg Monthly</span>
              </div>
              <p className="text-xl font-bold text-purple-400">{formatNumber(burnData?.statistics?.avg_monthly_burn || 0)}</p>
              <p className="text-xs text-purple-400/50">per month</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-white/70">Burn Progress</span>
              <span className="text-sm font-bold text-primary-theme">{burnData?.burn_percentage?.toFixed(2) || 0}%</span>
            </div>
            <div className="h-4 bg-white/10 rounded-full overflow-hidden relative">
              <motion.div 
                className="h-full bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 rounded-full relative"
                initial={{ width: 0 }}
                animate={{ width: `${burnData?.burn_percentage || 0}%` }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
              </motion.div>
              {/* Milestone markers */}
              {[25, 50, 75].map(milestone => (
                <div 
                  key={milestone}
                  className="absolute top-0 bottom-0 w-px bg-white/30"
                  style={{ left: `${milestone}%` }}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-white/30 mt-1">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
          
          {/* Monthly Chart */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary-theme" />
              Monthly Burn Report
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={burnData?.monthly_data || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickLine={false}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Bar dataKey="burned" radius={[4, 4, 0, 0]}>
                    {(burnData?.monthly_data || []).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={`url(#burnGradient)`}
                      />
                    ))}
                  </Bar>
                  <defs>
                    <linearGradient id="burnGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF4D00" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#FF4D00" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Additional Stats & Recent Burns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-xl">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-orange-400" />
                Burn Statistics
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-white/50 text-sm">Highest Monthly Burn</span>
                  <span className="text-orange-400 font-bold">
                    {formatNumber(burnData?.statistics?.highest_monthly_burn || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50 text-sm">Active Burn Months</span>
                  <span className="text-white font-bold">
                    {burnData?.statistics?.active_burn_months || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50 text-sm">Est. Months to Complete</span>
                  <span className="text-blue-400 font-bold">
                    {burnData?.statistics?.projected_months_to_complete || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-400" />
                Recent Burns
              </h4>
              <RecentBurnsList transactions={burnData?.recent_transactions} />
            </div>
          </div>
          
          {/* Footer Info */}
          <div className="mt-4 flex items-center justify-between text-xs text-white/30">
            <span>Burns executed monthly • Data updates hourly</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="flex items-center gap-1 hover:text-white/50 transition-colors">
                    <Info className="w-3 h-3" />
                    <span>How it works</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Tokens used for platform fees are sent to the burn wallet and permanently destroyed at the end of each month, reducing total supply.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
        
        <style jsx>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .animate-shimmer {
            animation: shimmer 2s infinite;
          }
        `}</style>
      </Card>

      {/* Record Burn Modal */}
      <Dialog open={showRecordModal} onOpenChange={setShowRecordModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Record Burn Transaction
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Log a new token burn event to the tracker
            </DialogDescription>
          </DialogHeader>
          <RecordBurnForm 
            onSuccess={handleRecordSuccess}
            onClose={() => setShowRecordModal(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export { BurnTracker };
export default BurnTracker;
